import React, { useState, useEffect } from 'react';
import { monumentChartRow1, monumentChartRow2 } from '../utils/hebrewMapping';
import { arabicChartRow1, arabicChartRow2 } from '../utils/arabicMapping';
import { chineseChartRow1, chineseChartRow2 } from '../utils/chineseMapping';
import { getLogoDataUrl, LOGO_URL } from '../utils/logoLoader';

const ProofSheet = ({ text, title = 'Monument Inscription Proof', language }) => {
  const [logoSrc, setLogoSrc] = useState(LOGO_URL);
  useEffect(() => {
    getLogoDataUrl().then(setLogoSrc);
  }, []);

  const accessor = language?.accessor || 'hebrew';
  const isArabic = accessor === 'arabic';
  const isChinese = accessor === 'simplified';
  
  // Select chart rows based on language
  let chartRow1, chartRow2;
  if (isChinese) {
    chartRow1 = chineseChartRow1;
    chartRow2 = chineseChartRow2;
  } else if (isArabic) {
    chartRow1 = arabicChartRow1;
    chartRow2 = arabicChartRow2;
  } else {
    chartRow1 = monumentChartRow1;
    chartRow2 = monumentChartRow2;
  }
  
  const proofTitle = language?.proofTitle || 'MONUMENT HEBREW';
  const invocation = language?.invocation || 'בס״ד';
  const invocationFontClass = language?.invocationFont || 'font-hebrew';
  
  // Select font based on language
  let cellFontFamily;
  if (isChinese) {
    cellFontFamily = '"Noto Serif SC", "Source Han Serif SC", serif';
  } else if (isArabic) {
    cellFontFamily = '"Noto Naskh Arabic", Amiri, serif';
  } else {
    cellFontFamily = 'Frank Ruhl Libre, serif';
  }
  
  const inlineFontClass = isChinese ? 'font-chinese' : (isArabic ? 'font-arabic' : 'font-hebrew');

  // Split text into lines - max 4 lines
  const lines = text.split('\n').slice(0, 4);

  // Convert each line into array of characters - max 23 per line.
  // Place the FIRST typed character at the rightmost cell (index 22), the
  // second at index 21, and so on. Empty cells pad the left side.
  const lineArrays = lines.map((line) => {
    const chars = line.split('').slice(0, 23);
    const cells = Array(23).fill('');
    chars.forEach((c, i) => {
      cells[22 - i] = c;
    });
    return cells;
  });
  while (lineArrays.length < 4) lineArrays.push(Array(23).fill(''));

  const lineLabels = ['FIRST LINE', 'SECOND LINE', 'THIRD LINE', 'FOURTH LINE'];

  // Inline style used inside every inscription cell to lock each character into
  // its own cell during html2canvas export (prevents Hebrew bidi reordering).
  const cellCharStyle = {
    direction: 'ltr',
    unicodeBidi: 'isolate',
    textAlign: 'center',
    display: 'block',
    lineHeight: '1',
  };

  return (
    <div
      className="proof-sheet bg-white p-8 font-serif"
      style={{ width: '8.5in', minHeight: '11in', border: '8px solid black', boxSizing: 'border-box' }}
    >
      {/* Top header: logo (left) + בס״ד (right) */}
      <div className="flex items-center justify-between mb-4">
        <img
          src={logoSrc}
          alt="Monument Logo"
          style={{ width: '64px', height: '64px', objectFit: 'contain' }}
          data-testid="proof-logo"
        />
        <div
          className={`${invocationFontClass} font-bold`}
          style={{ fontSize: '28px', lineHeight: '1', alignSelf: 'flex-start', marginTop: '-4px' }}
          data-testid="proof-bsd"
        >
          {invocation}
        </div>
      </div>

      {/* Title */}
      <div className="text-center mb-8">
        <h1 className="text-5xl font-bold tracking-wider" style={{ fontFamily: 'serif' }}>
          {proofTitle}
        </h1>
      </div>

      {/* Reference Chart */}
      <div className="mb-0 flex justify-center">
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(14, 52px)' }}>
            {chartRow1.map((letter, index) => (
              <div
                key={`chart-row1-${letter.english}`}
                style={{
                  width: '52px',
                  height: '80px',
                  border: '1px solid black',
                  boxSizing: 'border-box',
                  position: 'relative',
                  marginLeft: index === 0 ? 0 : '-1px',
                }}
              >
                {/* English header (top 26px) */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '26px',
                    borderBottom: '1px solid black',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    fontFamily: 'serif',
                    lineHeight: '1',
                    paddingBottom: '6px',
                  }}
                >
                  {letter.english}
                </div>
                {/* Foreign letter (remaining) */}
                <div
                  style={{
                    position: 'absolute',
                    top: '26px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: cellFontFamily,
                    fontSize: '26px',
                    lineHeight: '1',
                    overflow: 'hidden',
                    direction: 'ltr',
                    unicodeBidi: 'bidi-override',
                    paddingBottom: '10px',
                  }}
                >
                  {letter[accessor]}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(14, 52px)',
              marginTop: '4px',
            }}
          >
            {chartRow2.map((letter, index) => (
              <div
                key={`chart-row2-${letter.english}`}
                style={{
                  width: '52px',
                  height: '80px',
                  border: '1px solid black',
                  boxSizing: 'border-box',
                  position: 'relative',
                  marginLeft: index === 0 ? 0 : '-1px',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '26px',
                    borderBottom: '1px solid black',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    fontFamily: 'serif',
                    lineHeight: '1',
                    paddingBottom: '6px',
                  }}
                >
                  {letter.english}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: '26px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: cellFontFamily,
                    fontSize: '26px',
                    lineHeight: '1',
                    overflow: 'hidden',
                    direction: 'ltr',
                    unicodeBidi: 'bidi-override',
                    paddingBottom: '10px',
                  }}
                >
                  {letter[accessor]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inscription Section */}
      <div className="space-y-6" style={{ marginTop: '-6px' }}>
        <h3
          className="text-2xl font-bold text-center mb-8"
          style={{ fontFamily: 'serif' }}
        >
          INSCRIPTION
        </h3>

        {lineArrays.map((lineChars, lineIndex) => {
          const cellW = 30;
          const cellH = 45;
          const totalW = 23 * cellW;
          return (
            <div key={lineIndex} className="mb-8">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  direction: 'ltr',
                  marginBottom: '8px',
                }}
              >
                {/* Left line label */}
                <div
                  className="text-sm font-bold"
                  style={{
                    width: '50px',
                    minWidth: '50px',
                    maxWidth: '50px',
                    textAlign: 'center',
                    lineHeight: '1.1',
                    paddingRight: '8px',
                    flexShrink: 0,
                  }}
                >
                  Line
                  <br />
                  {lineIndex + 1}
                </div>

                {/* Absolutely-positioned inscription cells.
                    Using absolute positioning for each cell so html2canvas
                    cannot bidi-reorder them during PNG capture. */}
                <div
                  style={{
                    position: 'relative',
                    width: `${totalW}px`,
                    height: `${cellH}px`,
                    direction: 'ltr',
                  }}
                >
                  {lineChars.map((char, charIndex) => (
                    <div
                      key={`line-${lineIndex}-cell-${charIndex}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: `${charIndex * cellW}px`,
                        width: `${cellW + 1}px`,
                        height: `${cellH}px`,
                        border: '1px solid black',
                        backgroundColor: 'white',
                        boxSizing: 'border-box',
                        textAlign: 'center',
                        lineHeight: `${cellH}px`,
                        fontFamily: cellFontFamily,
                        fontSize: '20px',
                        overflow: 'hidden',
                        direction: 'ltr',
                        unicodeBidi: 'bidi-override',
                      }}
                    >
                      <span style={cellCharStyle}>{char || '\u00A0'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Centered label below */}
              <div
                className="text-center font-bold text-xl"
                style={{ fontFamily: 'serif', marginTop: '-12px' }}
              >
                {lineLabels[lineIndex]}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-12 text-center text-xs text-stone-600">
        <p>
          Generated:{' '}
          {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </div>
    </div>
  );
};

export default ProofSheet;
